/**
 * ProtectedRoute — wraps a route element and redirects to /login when the
 * current user has no valid qryptauth session.
 *
 * Passes the current location as `state.from` so the LoginPage can redirect
 * back after a successful login.
 */
import type { ReactNode } from 'react'; // rationale: type-only import for children prop
import { Navigate, useLocation } from 'react-router-dom'; // rationale: declarative redirect and current location access
import { useAuth } from './useAuth'; // rationale: resolves current session state from /auth/me

interface ProtectedRouteProps {
    children: ReactNode; // protected route content rendered only when authenticated
}

/**
 * Renders children when authenticated; redirects to /login otherwise.
 * Shows a loading indicator while the session check is in flight.
 *
 * @param children — content to render for authenticated users
 */
const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
    console.log('ProtectedRoute is running');
    const { loading, authenticated } = useAuth(); // probe /auth/me to determine session state
    const location = useLocation(); // capture current path so LoginPage can redirect back

    if (loading) {
        console.log('ProtectedRoute is returning loading state');
        return (
            <div className="protected-route-loading" role="status" aria-live="polite">
                Checking session…
            </div>
        );
    }

    if (!authenticated) {
        console.log('ProtectedRoute is returning redirect to /login');
        return <Navigate to="/login" state={{ from: location }} replace />; // pass from location for post-login redirect
    }

    console.log('ProtectedRoute is returning children');
    return <>{children}</>;
};

export default ProtectedRoute;
